import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { BehaviorSubject, Observable } from 'rxjs';
import { TradingFormState } from '../widgets/trading-form/trading-form.types';
import { sanitizeTradingFormState } from './form-state-sanitizer';

export type DraftFormState = TradingFormState | null;

type DraftStateContextValue = {
  state$: Observable<DraftFormState>;
  updateState: (draftState: TradingFormState) => void;
  clearState: () => void;
};

const DraftStateContext = createContext<DraftStateContextValue | undefined>(
  undefined
);

export const DraftStateProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const subject$ = useMemo(() => new BehaviorSubject<DraftFormState>(null), []);

  const updateState = useCallback(
    (draftState: TradingFormState) => {
      subject$.next(sanitizeTradingFormState(draftState));
    },
    [subject$]
  );

  const clearState = useCallback(() => {
    subject$.next(null);
  }, [subject$]);

  useEffect(() => {
    return () => {
      subject$.next(null);
      subject$.complete();
    };
  }, [subject$]);

  const value = useMemo<DraftStateContextValue>(
    () => ({
      state$: subject$.asObservable(),
      updateState,
      clearState
    }),
    [clearState, subject$, updateState]
  );

  return React.createElement(DraftStateContext.Provider, { value }, children);
};

export const useDraftState = () => {
  const context = useContext(DraftStateContext);
  if (!context) {
    throw new Error('useDraftState must be used within DraftStateProvider');
  }

  return context;
};
